import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useHttp } from "../hooks/useHttp";

interface ClaimedChip {
  chipUid: string;
  toyType: number;
  emailAddress: string;
  claimedAt: string;
  redeemUrl: string | null;
}

const CAT_TYPE_NAMES: Record<number, string> = {
  1: "Molly",
  2: "Tabby",
  3: "Rascal",
  4: "Smudge",
  5: "Luna",
  6: "Tiger",
};

export const CatGameReward = () => {
  const navigate = useNavigate();
  const { sendRequest } = useHttp();

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [claimedChips, setClaimedChips] = useState<ClaimedChip[]>([]);
  const [redeemedChips, setRedeemedChips] = useState<Set<string>>(new Set());

  useEffect(() => {
    const checkAuthAndLoadChips = async () => {
      try {
        const { data: sessionData } = await sendRequest<{
          authenticated: boolean;
          email?: string;
        }>({
          endpoint: "/catgame/session",
          method: "GET",
          responseType: "json",
        });

        if (!sessionData.authenticated) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        setIsAuthenticated(true);

        const { data: chipsData } = await sendRequest<{
          success: boolean;
          chips: ClaimedChip[];
        }>({
          endpoint: "/catgame/my-chips",
          method: "GET",
          responseType: "json",
        });

        if (chipsData.success) {
          const chipsWithRedeemUrl = chipsData.chips.filter(
            (chip) => chip.redeemUrl
          );
          setClaimedChips(chipsWithRedeemUrl);
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        console.error("Failed to load data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndLoadChips();
  }, [sendRequest]);

  const handleRedeemReward = (chipUid: string, redeemUrl: string) => {
    if (!redeemUrl) {
      return;
    }

    setRedeemedChips((prev) => {
      const newSet = new Set(prev);
      newSet.add(chipUid);
      return newSet;
    });

    const opened = window.open(redeemUrl, "_blank", "noopener,noreferrer");
    if (!opened) {
      window.location.href = redeemUrl;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100dvh-149.5px)] p-6">
        <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="px-6 py-8">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Not Logged In</h1>

          <p className="mb-6 text-[#666666]">
            Please login to view your in-game rewards.
          </p>

          <button
            onClick={() => navigate("/rewards/catgame")}
            className="px-6 py-3 text-white bg-black rounded-lg transition-colors hover:bg-gray-800"
          >
            Go to CatGame
          </button>
        </div>
      </div>
    );
  }

  if (claimedChips.length === 0) {
    return (
      <div className="px-6 py-8">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">No Rewards Available</h1>

          <p className="mb-6 text-[#666666]">
            You don't have any in-game rewards to claim yet.
          </p>

          <button
            onClick={() => navigate("/rewards/catgame")}
            className="px-6 py-3 text-white bg-black rounded-lg transition-colors hover:bg-gray-800"
          >
            Go to CatGame
          </button>
        </div>
      </div>
    );
  }

  const gradients = [
    { from: "#4CAF50", to: "#45A049", buttonColor: "#4CAF50" },
    { from: "#FF6B6B", to: "#FF8E53", buttonColor: "#FF6B6B" },
  ];

  return (
    <div className="px-6">
      <div className="mb-3">
        <h1 className="mb-1.5 text-2xl font-bold">CatGame Rewards</h1>

        <p className="text-[#666666]">
          Redeem your in-game rewards for CatGame!
        </p>
      </div>

      <div className="mb-4 space-y-3">
        {claimedChips.map((chip) => {
          const isRedeemed = redeemedChips.has(chip.chipUid);
          const gradient = gradients[isRedeemed ? 0 : 1];
          const catName = CAT_TYPE_NAMES[chip.toyType] || `Type ${chip.toyType}`;

          return (
            <div
              key={chip.chipUid}
              className="flex justify-between items-center p-4 w-full rounded-lg"
              style={{
                backgroundImage: `linear-gradient(to right, ${gradient.from}, ${gradient.to})`,
              }}
            >
              <div className="flex gap-3 items-center">
                <div className="flex items-center justify-center w-[48px] h-[48px] bg-[#FFFFFF33] rounded-[10px]">
                  <img
                    src="/images/reward-open.png"
                    alt="gift"
                    className="max-w-[32px]"
                  />
                </div>

                <h6 className="font-bold text-white">
                  {catName} #{chip.chipUid?.trim().slice(-6) || "N/A"}
                </h6>
              </div>

              <button
                onClick={() => handleRedeemReward(chip.chipUid, chip.redeemUrl!)}
                disabled={isRedeemed}
                className="w-[78px] h-[31px] rounded-[50px] bg-white font-bold text-xs disabled:opacity-70"
                style={{
                  color: isRedeemed ? "#666" : gradient.buttonColor,
                }}
              >
                {isRedeemed ? "Redeemed" : "Redeem"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CatGameReward;
